import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ImagePlus } from 'lucide-react';
import { DashboardShell } from '../layout/DashboardShell';
import { UserMenu } from '../components/UserMenu';
import { DashPanel } from '../components/DashPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEvents } from '../context/EventsContext';
import type { EventType } from '../types/events';
import { EVENT_TYPE_LABELS } from '../types/events';
import { resizeImageToDataUrl } from '../utils/imageResize';

function emptyState() {
  return {
    title: '',
    photoUrl: '',
    photoName: '',
    local: '',
    type: 'oficina' as EventType,
    customType: '',
    noPeriod: false,
    startDate: '',
    startTime: '08:00',
    endDate: '',
    endTime: '17:00',
  };
}

export default function EventFormPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { getEventById, createEvent, updateEvent } = useEvents();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const event = eventId ? getEventById(eventId) : undefined;
  const isEdit = Boolean(eventId);
  const [error, setError] = useState<string | null>(null);

  const [state, setState] = useState(() =>
    event
      ? {
          title: event.title,
          photoUrl: event.photoUrl,
          photoName: '',
          local: event.school,
          type: event.type,
          customType: event.customTypeLabel ?? '',
          noPeriod: Boolean(event.noPeriod),
          startDate: event.startDate,
          startTime: event.startTime,
          endDate: event.endDate,
          endTime: event.endTime,
        }
      : emptyState()
  );

  useEffect(() => {
    if (isEdit && !event) navigate('/dashboard', { replace: true });
  }, [isEdit, event, navigate]);

  const set = <K extends keyof ReturnType<typeof emptyState>>(
    key: K,
    value: ReturnType<typeof emptyState>[K]
  ) => setState((prev) => ({ ...prev, [key]: value }));

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const resized = await resizeImageToDataUrl(file);
    set('photoUrl', resized);
    set('photoName', file.name);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const input = {
      title: state.title,
      photoUrl: state.photoUrl,
      school: state.local,
      type: state.type,
      customTypeLabel: state.type === 'outro' ? state.customType : undefined,
      noPeriod: state.noPeriod,
      startDate: state.noPeriod ? '' : state.startDate,
      startTime: state.noPeriod ? '' : state.startTime,
      endDate: state.noPeriod ? '' : state.endDate,
      endTime: state.noPeriod ? '' : state.endTime,
      collaboratorEmails: [] as string[],
    };

    try {
      if (isEdit && event) {
        updateEvent(event.id, input);
        navigate(`/dashboard/events/${event.id}`);
        return;
      }

      const created = createEvent(input);
      navigate(`/dashboard/events/${created.id}`);
    } catch (err) {
      console.error('Falha ao salvar evento', err);
      setError('Não foi possível salvar o evento. Tente novamente.');
    }
  };

  return (
    <DashboardShell mode="home">
      <header className="shrink-0 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => navigate(isEdit && event ? `/dashboard/events/${event.id}` : '/dashboard')}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Voltar
          </Button>
          <div>
            <h1 className="text-base font-semibold text-foreground">
              {isEdit ? 'Editar evento' : 'Criar evento de pesquisa'}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Dados filtrados por intervalo: data/hora inicial até data/hora final.
            </p>
          </div>
        </div>
        <UserMenu />
      </header>

      <div className="flex-1 overflow-y-auto p-6 bg-blue-100 dark:bg-background border-t border-l border-border rounded-tl-2xl">
        <form onSubmit={handleSubmit} className="max-w-2xl">
        <DashPanel className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="title">Título</Label>
            <Input id="title" required value={state.title} onChange={(e) => set('title', e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>Foto do evento</Label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhoto}
            />
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start gap-2 sm:w-auto"
              onClick={() => fileRef.current?.click()}
            >
              <ImagePlus className="h-4 w-4" />
              {state.photoName || 'Escolher da galeria'}
            </Button>
            {state.photoUrl && (
              <img
                src={state.photoUrl}
                alt=""
                className="w-full max-w-md h-40 object-cover rounded-lg border border-border"
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="local">Local</Label>
              <Input
                id="local"
                required
                placeholder="Ex.: Escola Mãe Angélica"
                value={state.local}
                onChange={(e) => set('local', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={state.type} onValueChange={(v) => set('type', v as EventType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(EVENT_TYPE_LABELS).map(([k, label]) => (
                    <SelectItem key={k} value={k}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {state.type === 'outro' && (
            <div className="space-y-2 max-w-md">
              <Label htmlFor="customType">Especifique o tipo</Label>
              <Input
                id="customType"
                required
                placeholder="Ex.: Avaliação diagnóstica"
                value={state.customType}
                onChange={(e) => set('customType', e.target.value)}
              />
            </div>
          )}

          <div className="space-y-3 max-w-md rounded-lg border border-border p-3">
            {/* Opção removida da criação (todo evento novo passa a ter período
                obrigatório) — só continua visível aqui se o evento já existente
                sendo editado já usava esse modo, pra não travar quem editar um
                evento antigo sem poder desmarcar. */}
            {isEdit && state.noPeriod && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={state.noPeriod}
                  onCheckedChange={(checked) => set('noPeriod', Boolean(checked))}
                />
                <span className="text-sm text-foreground">
                  Não usar período — sincronizar automaticamente todos os dados do Firebase
                </span>
              </label>
            )}

            {!state.noPeriod && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="start">Data inicial</Label>
                  <Input
                    id="start"
                    type="date"
                    required={!state.noPeriod}
                    value={state.startDate}
                    onChange={(e) => set('startDate', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="startTime">Horário inicial</Label>
                  <Input
                    id="startTime"
                    type="time"
                    required={!state.noPeriod}
                    value={state.startTime}
                    onChange={(e) => set('startTime', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end">Data final</Label>
                  <Input
                    id="end"
                    type="date"
                    required={!state.noPeriod}
                    value={state.endDate}
                    onChange={(e) => set('endDate', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endTime">Horário final</Label>
                  <Input
                    id="endTime"
                    type="time"
                    required={!state.noPeriod}
                    value={state.endTime}
                    onChange={(e) => set('endTime', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </DashPanel>

          <div className="flex justify-end gap-2 pt-5">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(isEdit && event ? `/dashboard/events/${event.id}` : '/dashboard')}
            >
              Cancelar
            </Button>
            <Button type="submit">{isEdit ? 'Salvar alterações' : 'Criar evento'}</Button>
          </div>
        </form>
      </div>
    </DashboardShell>
  );
}
